import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { userFilesApi } from 'api';
import { Skeleton } from 'primereact/skeleton';
import { BreadCrumb } from 'primereact/breadcrumb';
import { RouterUrlParams } from 'App';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { usePanel } from 'contexts/panelContext';
import { NewUploadUserFilesDialog } from 'components/session/yourNotes/NewUploadUserFilesDialog';
import { DispalyModule } from './DisplayModule';

export const DispalyPage = () => {
	const { t } = useTranslation();
	const params = useParams();
	const userFileId = Number(params.userFileId);
	const { organizationName, programName, type, pairId } = useParams() as RouterUrlParams;
	const { currentApplicationData } = usePanel();

	const [editDialogOpen, setEditDialogOpen] = useState(false);

	const {
		data: getUserfileData,
		isSuccess: getUserfileSuccess,
		isLoading: getUserfileLoading,
		isFetched: getUserfileFetched,
		refetch,
	} = useQuery(['userfile', { userFileId }], () => userFilesApi.getUserfile(Number(userFileId)), {
		enabled: Boolean(userFileId),
	});

	const navigate = useNavigate();
	const homeBreadcrumb = {
		icon: 'pi pi-book',
		command: () => {
			navigate(`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/userfiles`);
		},
	};

	const isAuthor =
		Number(getUserfileData?.userFile?.authorApplicationId ?? getUserfileData?.userFile?.authorApplication?.id) ===
		Number(currentApplicationData?.id);

	return (
		<>
			<Helmet title={t('userPanel.userFiles.title')} />
			<SubPageTitle
				title={t('userPanel.userFiles.title')}
				right={
					isAuthor ? (
						<Button
							icon="pen-to-square"
							iconStyle="solid"
							label={t('actions.edit')}
							onClick={() => setEditDialogOpen(true)}
						/>
					) : undefined
				}
			/>

			{getUserfileLoading && (
				<>
					<div className="mb-3">
						<Skeleton width="100%" height="2.5rem" className="mb-2" />
					</div>
					<Box variant="white-bordered">
						<BoxSection>
							<Skeleton width="100%" height="3rem" className="mb-3" />
							<Skeleton width="100%" height="20rem" />
						</BoxSection>
					</Box>
				</>
			)}

			{getUserfileSuccess && (
				<>
					<BreadCrumb
						model={[
							{
								label: t('userPanel.userFiles.title'),
								command: () => {
									navigate(
										`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/userfiles`
									);
								},
							},
							{
								label: `${t('userPanel.userFiles.file.session')} ${
									getUserfileData.userFile.session?.sessionNumber || '-'
								}`,
								command: () => {
									navigate(
										`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/session/${getUserfileData.userFile.sessionId}/notes`
									);
								},
							},
							{
								label: getUserfileData.userFile.name,
							},
						]}
						home={homeBreadcrumb}
						className="mb-3"
					/>
					<Box variant="white-bordered">
						<BoxSection>
							<DispalyModule userFileId={userFileId} />
						</BoxSection>
					</Box>

					{isAuthor && (
						<NewUploadUserFilesDialog
							visible={editDialogOpen}
							onHide={() => {
								setEditDialogOpen(false);
								refetch();
							}}
							userFile={getUserfileData.userFile}
							onFinish={() => {
								refetch();
							}}
							onSave={() => {
								refetch();
							}}
						/>
					)}
				</>
			)}
		</>
	);
};
